import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { VaultNoticeService, type Notice } from '../../../core/services/vault-notice.service';

/** Beyond this the stack stops being a message and starts being a wall. */
const MAX_VISIBLE = 3;

/**
 * Renders the vault's notices, bottom-right.
 *
 * **Two permanent live regions, not one per card.** The committee module's toast puts
 * `role="status"` on each toast at the moment it is created, and screen readers routinely miss those
 * — a live region has to exist *before* content lands in it to be announced. So both regions sit in
 * the DOM from the start, empty, and cards are inserted into them: `role="alert"` for errors, which
 * should interrupt, and `aria-live="polite"` for everything else, which should not.
 *
 * Sits at `z-[120]`, above the vault's modals at `z-[80]`. That is deliberate: the add-document
 * dialog stays open when some files fail, and the notice saying which ones has to be readable over
 * it.
 */
@Component({
  selector: 'app-vault-notice-host',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible().length) {
      <!--
        Hovering or focusing anywhere in the stack holds every countdown — WCAG 2.2.1. The CSS pauses
        the draining hairline; the service pauses the timer behind it. Both are needed: one is what
        you see, the other is what actually happens.
      -->
      <div
        class="notice-stack fixed z-[120] flex flex-col pointer-events-none
               bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[24rem]"
        (mouseenter)="notices.pauseAll()"
        (mouseleave)="notices.resumeAll()"
        (focusin)="notices.pauseAll()"
        (focusout)="notices.resumeAll()">

        @if (visible().length > 1) {
          <div class="flex justify-end">
            <button
              type="button"
              (click)="notices.clear()"
              class="pointer-events-auto vault-label px-2 py-1 rounded-md
                     hover:text-ink transition-colors">
              Dismiss all
            </button>
          </div>
        }

        <!--
          Errors first, so they sit above the run of successes rather than being pushed off the top
          of the stack by them.
        -->
        <div role="alert" class="flex flex-col">
          @for (notice of errors(); track notice.id) {
            <ng-container *ngTemplateOutlet="card; context: { $implicit: notice }" />
          }
        </div>

        <div aria-live="polite" class="flex flex-col">
          @for (notice of polite(); track notice.id) {
            <ng-container *ngTemplateOutlet="card; context: { $implicit: notice }" />
          }
        </div>
      </div>
    }

    <ng-template #card let-notice>
      <div
        class="notice-in notice-card pointer-events-auto mt-3 overflow-hidden
               bg-paper border border-rule rounded-xl"
        [class.notice-card--success]="notice.kind === 'success'"
        [class.notice-card--error]="notice.kind === 'error'"
        [class.notice-card--info]="notice.kind === 'info'">

        <div class="flex items-start gap-3 p-4">
          <span
            class="notice-card__icon shrink-0 w-8 h-8 rounded-full grid place-items-center"
            aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              @switch (notice.kind) {
                @case ('success') {
                  <path d="M20 6L9 17l-5-5" />
                }
                @case ('error') {
                  <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L14.7 3.9a2 2 0 00-3.4 0z" />
                  <path d="M12 9v4M12 17h.01" />
                }
                @default {
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5M12 8h.01" />
                }
              }
            </svg>
          </span>

          <div class="min-w-0 flex-1">
            <div class="flex items-start gap-2">
              <p class="text-sm font-medium text-ink flex-1 min-w-0">{{ notice.title }}</p>

              <!-- The same thing twice reads as one event that repeated, not as a glitch. -->
              @if (notice.count > 1) {
                <span
                  class="vault-figure shrink-0 text-[0.6875rem] leading-none px-1.5 py-1
                         rounded-full border border-rule text-ink-soft"
                  [attr.aria-label]="'Repeated ' + notice.count + ' times'">
                  &times;{{ notice.count }}
                </span>
              }
            </div>

            @if (notice.detail) {
              <p class="text-sm text-ink-soft mt-1 break-words">{{ notice.detail }}</p>
            }
          </div>

          <button
            type="button"
            (click)="notices.dismiss(notice.id)"
            [attr.aria-label]="'Dismiss: ' + notice.title"
            class="tap-target shrink-0 -mr-2 -mt-2 w-9 h-9 grid place-items-center rounded-lg
                   text-ink-soft hover:text-ink transition-colors">
            <span class="text-lg leading-none" aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Absent on errors, which never time out. -->
        @if (notice.durationMs > 0) {
          <span
            class="notice-card__progress"
            [style.--notice-duration.ms]="notice.durationMs"
            aria-hidden="true"></span>
        }
      </div>
    </ng-template>
  `,
})
export class VaultNoticeHostComponent {
  readonly notices = inject(VaultNoticeService);

  /**
   * The few worth showing. A stack taller than the screen helps nobody, so older cards fall off —
   * but **errors are dropped last**.
   *
   * Taking the newest three outright looked right and was not: an error sits there until it is
   * acknowledged, so it is the one card that might still need acting on, and three later "saved"
   * messages would quietly push it out. Successes are the ones that can afford to be missed.
   */
  readonly visible = computed(() => {
    const all = this.notices.notices();
    if (all.length <= MAX_VISIBLE) return all;

    const keptErrors = all.filter(n => n.kind === 'error').slice(-MAX_VISIBLE);
    const keptRest = all.filter(n => n.kind !== 'error').slice(-(MAX_VISIBLE - keptErrors.length));

    // Re-filtered against the original list so the survivors stay in arrival order and the stack
    // does not reshuffle itself as cards expire.
    return all.filter(n => keptErrors.includes(n) || keptRest.includes(n));
  });

  readonly errors = computed(() => this.visible().filter(n => n.kind === 'error'));
  readonly polite = computed<Notice[]>(() => this.visible().filter(n => n.kind !== 'error'));
}
